import { useEffect, useMemo, useRef, useState } from 'react';
import { toAbsoluteUrl } from '../../../assets/helpers/AssetHelpers';
import {
	getNews,
	getArticles,
	getHeaderCirculars,
	getHeaderBlogs,
	getStockMasterSearch,
} from '../../../services/api/Api';
import {
	headerSearchCategories,
	HeaderSearchCategory,
	HeaderSearchResultItem,
	NewsApiResponse,
	ArticleApiItem,
	WpPostItem,
	StockSearchApiResponse,
} from './headerSearchConstants';
import { useSearchCategory } from './useSearchCategory';

const NEWS_RESULT_LIMIT = 25;
const ARTICLES_RESULT_LIMIT = 25;
const CIRCULARS_RESULT_LIMIT = 25;
const BLOGS_RESULT_LIMIT = 25;
const STOCKS_RESULT_LIMIT = 25;
const SEARCH_DEBOUNCE_MS = 400;

const getNewsUrl = (item: { id: string | number; title: string }) =>
	`${process.env.REACT_APP_MAIN_WEBSITE_URL}news/?title=${encodeURIComponent(item.title)}&news_id=${item.id}`;

const getArticleUrl = (item: { id: string | number; title: string }) =>
	`${process.env.REACT_APP_MAIN_WEBSITE_URL}news/?article_title=${encodeURIComponent(item.title)}&article_id=${item.id}`;

const getCircularUrl = (slug: string) =>
	`${process.env.REACT_APP_MAIN_WEBSITE_URL}circulars/?title=${encodeURIComponent(slug)}`;

const searchResultTagLabels: Partial<Record<HeaderSearchCategory, string>> = {
	Blogs: 'Post',
	Circulars: 'Circular',
};

const getSearchResultTag = (category: HeaderSearchCategory) => searchResultTagLabels[category] ?? category;

const filterByTitleOrDescription = (items: HeaderSearchResultItem[], term: string) => {
	const lowerTerm = term.toLowerCase();
	return items.filter(
		(item) =>
			item.title?.toLowerCase().includes(lowerTerm) ||
			item.description?.toLowerCase().includes(lowerTerm),
	);
};

export const useHeaderSearch = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
	const [activeSearchCategory, setActiveSearchCategory] = useState<HeaderSearchCategory>(
		headerSearchCategories[0],
	);
	const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
	const searchWrapperRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const trimmedSearchTerm = searchTerm.trim();

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
				setIsSearchDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		if (isMobileSearchOpen) {
			searchInputRef.current?.focus();
		}
	}, [isMobileSearchOpen]);

	const news = useSearchCategory<NewsApiResponse>({
		category: 'News',
		activeCategory: activeSearchCategory,
		searchTerm: trimmedSearchTerm,
		fetchOnce: true,
		fetcher: () => getNews() as Promise<NewsApiResponse>,
		mapItems: (response) =>
			(response?.items || []).map((item) => ({
				id: item.guid,
				title: item.title,
				description: item.description,
				url: getNewsUrl({ id: item.guid, title: item.title }),
			})),
	});

	const articles = useSearchCategory<ArticleApiItem[]>({
		category: 'Articles',
		activeCategory: activeSearchCategory,
		searchTerm: trimmedSearchTerm,
		fetchOnce: true,
		fetcher: () => getArticles() as Promise<ArticleApiItem[]>,
		mapItems: (response) =>
			(response || []).map((item) => ({
				id: item._id,
				title: item.article?.title ?? '',
				description: item.article?.description,
				url: getArticleUrl({ id: item._id, title: item.article?.title ?? '' }),
			})),
	});

	const circulars = useSearchCategory<WpPostItem[]>({
		category: 'Circulars',
		activeCategory: activeSearchCategory,
		searchTerm: trimmedSearchTerm,
		debounceMs: SEARCH_DEBOUNCE_MS,
		fetcher: (term) => getHeaderCirculars(term) as Promise<WpPostItem[]>,
		mapItems: (response) =>
			(response || [])
				.slice(0, CIRCULARS_RESULT_LIMIT)
				.map((item) => ({
					id: item.id,
					title: item.title?.rendered ?? '',
					url: item.link,
					slug: item.slug,
				})),
	});

	const blogs = useSearchCategory<WpPostItem[]>({
		category: 'Blogs',
		activeCategory: activeSearchCategory,
		searchTerm: trimmedSearchTerm,
		debounceMs: SEARCH_DEBOUNCE_MS,
		fetcher: (term) => getHeaderBlogs(term) as Promise<WpPostItem[]>,
		mapItems: (response) =>
			(response || [])
				.slice(0, BLOGS_RESULT_LIMIT)
				.map((item) => ({ id: item.id, title: item.title?.rendered ?? '', url: item.link })),
	});

	const stocks = useSearchCategory<StockSearchApiResponse>({
		category: 'Stocks',
		activeCategory: activeSearchCategory,
		searchTerm: trimmedSearchTerm,
		debounceMs: SEARCH_DEBOUNCE_MS,
		isEnabled: isSearchDropdownOpen,
		cacheEmptyTermResult: true,
		fetcher: (term) => getStockMasterSearch(term) as Promise<StockSearchApiResponse>,
		mapItems: (response) =>
			(response?.data || []).slice(0, STOCKS_RESULT_LIMIT).map((item, index) => ({
				id: item.Slug || index,
				title: item.CompanyName,
				url: `${process.env.REACT_APP_MAIN_WEBSITE_URL}stocks/${item.Slug}/`,
			})),
	});

	const activeSearchResults = useMemo<HeaderSearchResultItem[]>(() => {
		switch (activeSearchCategory) {
			case 'Circulars':
				return circulars.items;
			case 'Blogs':
				return blogs.items;
			case 'News':
				return (trimmedSearchTerm ? filterByTitleOrDescription(news.items, trimmedSearchTerm) : news.items).slice(
					0,
					NEWS_RESULT_LIMIT,
				);
			case 'Articles':
				return (
					trimmedSearchTerm ? filterByTitleOrDescription(articles.items, trimmedSearchTerm) : articles.items
				).slice(0, ARTICLES_RESULT_LIMIT);
			case 'Stocks':
				return stocks.items;
			default:
				return [];
		}
	}, [activeSearchCategory, trimmedSearchTerm, news.items, articles.items, circulars.items, blogs.items, stocks.items]);

	const isLoadingActiveCategory =
		(activeSearchCategory === 'Circulars' && circulars.isLoading) ||
		(activeSearchCategory === 'Blogs' && blogs.isLoading) ||
		(activeSearchCategory === 'Stocks' && stocks.isLoading) ||
		(activeSearchCategory === 'News' && news.isLoading) ||
		(activeSearchCategory === 'Articles' && articles.isLoading);

	const renderSearchForm = (formClassName: string) => (
		<form
			className={formClassName}
			role="search"
			onSubmit={(e) => {
				e.preventDefault();
			}}>
			<div className="header-search position-relative" ref={searchWrapperRef}>
				<img src={toAbsoluteUrl('header-images/search.svg')} className="header-search-icon" alt="" />
				<input
					type="text"
					className="header-search-input"
					ref={searchInputRef}
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					onFocus={() => setIsSearchDropdownOpen(true)}
					aria-label="Search any Stocks, Blogs, Circulars"
				/>
				{!searchTerm && (
					<div className="header-search-placeholder-marquee" aria-hidden="true">
						<span>Search any Stocks, Blogs, Circulars</span>
					</div>
				)}
				{isSearchDropdownOpen && (
					<div className="header-search-dropdown">
						<div className="header-search-categories">
							{headerSearchCategories.map((category) => (
								<button
									key={category}
									type="button"
									className={`header-search-category-item${activeSearchCategory === category ? ' active' : ''
										}`}
									onClick={() => setActiveSearchCategory(category)}>
									{category}
									<i className="fas fa-chevron-right"></i>
								</button>
							))}
						</div>
						<div className="header-search-results">
							{isLoadingActiveCategory ? (
								<div className="header-search-no-result">
									<div className="spinner" aria-label="Loading"></div>
								</div>
							) : activeSearchResults.length > 0 ? (
								activeSearchResults.map((item) =>
									item.url ? (
										<a
											key={item.id}
											href={item.slug ? getCircularUrl(item.slug) : item.url}
											target="_blank"
											rel="noopener noreferrer"
											className="header-search-result-row">
											<span className="header-search-result-title">{item.title}</span>
											<span className="header-search-result-tag">{getSearchResultTag(activeSearchCategory)}</span>
										</a>
									) : (
										<div key={item.id} className="header-search-result-row">
											<span className="header-search-result-title">{item.title}</span>
											<span className="header-search-result-tag">{getSearchResultTag(activeSearchCategory)}</span>
										</div>
									)
								)
							) : (
								<div className="header-search-no-result">
									<img
										className="float-end mt-5 pt-4 wp-80"
										src={toAbsoluteUrl('/header-images/not_found.svg')}
										alt="No results found"
									/>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</form>
	);

	return {
		isMobileSearchOpen,
		openMobileSearch: () => setIsMobileSearchOpen(true),
		closeMobileSearch: () => setIsMobileSearchOpen(false),
		renderSearchForm,
	};
};
